import { useEffect, useRef } from 'react';
import {
    initAppInsights,
    setGlobalProperties,
    setAuthenticatedUser,
    clearAuthenticatedUser,
    trackEvent,
    hashEmail,
} from './appInsights';

/**
 * Global Application Insights provider for the questionnaire client.
 *
 * Responsibilities:
 * 1. Initialises the browser SDK once a connection string is available
 *    (fetched at runtime from the server's `/api/config`).
 * 2. Keeps custom dimensions (`authMode`) stamped on every telemetry item.
 * 3. Syncs the authenticated user context with the app's auth state, using a
 *    non-PII hashed identifier derived from the user's email.
 * 4. Emits enriched `PageViewed` custom events as the state-driven view changes
 *    (this app has no client-side router, so views are tracked explicitly).
 *
 * Renders its children unchanged.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.connectionString]
 * @param {'anonymous' | 'dynamics' | 'portal'} [props.authMode]
 * @param {string} [props.view] Top-level auth view: loading | login | verify | authenticated.
 * @param {string} [props.appView] Authenticated sub-view: table | survey.
 * @param {boolean} [props.isPreviewMode]
 * @param {string} [props.userEmail]
 * @param {boolean} [props.isAuthenticated]
 */
export function AppInsightsProvider({
    children,
    connectionString,
    authMode = 'anonymous',
    view,
    appView,
    isPreviewMode,
    userEmail,
    isAuthenticated,
}) {
    const initialisedRef = useRef(false);
    const prevPageRef = useRef(null);

    // ---------- 1. Initialise the SDK when a connection string arrives ----------
    useEffect(() => {
        if (initialisedRef.current || !connectionString) return;
        initAppInsights(connectionString, { authMode });
        initialisedRef.current = true;
    }, [connectionString, authMode]);

    // ---------- 2. Keep custom dimensions current ----------
    useEffect(() => {
        setGlobalProperties({ authMode });
    }, [authMode]);

    // ---------- 3. Sync authenticated user context ----------
    useEffect(() => {
        if (isAuthenticated && userEmail) {
            setAuthenticatedUser(hashEmail(userEmail));
        } else {
            clearAuthenticatedUser();
        }
    }, [isAuthenticated, userEmail]);

    // ---------- 4. Track enriched page views on view change ----------
    useEffect(() => {
        // Derive a human-readable page name from the current state.
        let page;
        if (isPreviewMode) {
            page = 'preview';
        } else if (view === 'authenticated') {
            page = appView === 'survey' ? 'survey' : 'table';
        } else {
            page = view; // loading | login | verify
        }

        if (!page || page === 'loading' || page === prevPageRef.current) return;
        prevPageRef.current = page;

        trackEvent('PageViewed', { page, authMode });
    }, [view, appView, isPreviewMode, authMode]);

    return children;
}
